/**
 * FlexCarousel — a swipe/scroll carousel whose cards lean and flex with
 * momentum and spring back when it settles (after Colton Tollett's
 * "flexible mesh" carousel, without the WebGL). Native horizontal scroll
 * with snap on touch; drag-to-scroll with inertia on desktop; a hairline
 * progress track underneath.
 */
import { Link } from "@remix-run/react";
import { useRef } from "react";
import { useFlexScroll } from "./useFlexScroll";

export interface FlexCarouselItem {
  label: string;
  sub?: string;
  price: string;
  img: string;
  to: string;
}

interface FlexCarouselProps {
  items: FlexCarouselItem[];
  className?: string;
  locale?: "vi" | "en";
}

export function FlexCarousel({ items, className = "", locale = "en" }: FlexCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLSpanElement>(null);
  const { dragging } = useFlexScroll(trackRef, { cardSelector: ".fx-card", mediaSelector: ".fx-media img", progressRef });

  return (
    <div className={`fx ${className}`}>
      <div ref={trackRef} className={`fx-track ${dragging ? "is-dragging" : ""}`} aria-label="Products">
        {items.map((it) => (
          <Link
            key={it.to + it.label}
            to={it.to}
            className="fx-card"
            draggable={false}
          >
            <div className="fx-media">
              <img src={it.img} alt={it.label} width={900} height={1125} loading="lazy" draggable={false} />
            </div>
            <div className="df-tile-label">
              <span>{it.label}{it.sub ? <span className="fx-sub"> · {it.sub}</span> : null}</span>
              <span className="text-[#737373]">{it.price}</span>
            </div>
          </Link>
        ))}
      </div>
      <div className="fx-rail" aria-hidden="true"><span ref={progressRef} className="fx-progress" /></div>
    </div>
  );
}
